import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Api from "../../../features/one-query/services/ApiServices";

export interface GetOptionsRequest {
  StartDate?: string;
  EndDate?: string;
  AppIds?: string[];
  Search?: string;
}

interface RegenerateData {
  historyId: string;
  connectionId: string;
}

//Get all text suite history items endpoint.
const getTextSuiteHistory = async (data?: GetOptionsRequest) => {
  const params = new URLSearchParams();
  if (data?.StartDate) params.append("StartDate", data.StartDate);
  if (data?.EndDate) params.append("EndDate", data.EndDate);
  if (data?.AppIds) params.append("AppIds", data.AppIds.join(","));
  if (data?.Search) params.append("search", data.Search);

  const response = await Api.get(`/TextSuiteHistory?${params.toString()}`);
  return response.data;
};

const useGetTextSuiteHistory = (filters?: GetOptionsRequest) => {
  return useQuery({
    queryKey: ["getTextSuiteHistory", filters],
    queryFn: () => getTextSuiteHistory(filters),
    refetchOnWindowFocus: false,
  });
};

//Get  text suite history filter apps endpoint.
const getTextSuiteHistoryApps = async () => {
  const response = await Api.get("/TextSuits/Apps");
  return response.data;
};

const useGetTextSuiteHistoryApps = () => {
  return useQuery({
    queryKey: ["getTextSuiteHistoryApps"],
    queryFn: () => getTextSuiteHistoryApps(),
    enabled:false
  });
};

//Delete all text suite history items endpoint.
const deleteTextSuiteHistory = async () => {
  const response = await Api.delete("/TextSuiteHistory");
  return response.data;
};

const useDeleteTextSuiteHistory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteTextSuiteHistory"],
    mutationFn: () => deleteTextSuiteHistory(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getTextSuiteHistory"] });
    },
  });
};

//Get text suite history item endpoint.
const getTextSuiteHistoryById = async (itemId: string) => {
  const response = await Api.get(`/TextSuiteHistory/${itemId}`);
  return response.data;
};

const useGetTextSuiteHistoryById = (itemId: string) => {
  return useQuery({
    queryKey: ["getTextSuiteHistoryById",itemId],
    queryFn: () => getTextSuiteHistoryById(itemId),
    enabled:!!itemId
  });
};

//Delete text suite history item endpoint.
const deleteTextSuiteHistoryById = async (itemId: string) => {
  const response = await Api.delete(`/TextSuiteHistory/${itemId}`);
  return response.data;
};

const useDeleteTextSuiteHistoryById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteTextSuiteHistoryById"],
    mutationFn: (itemId: string) => deleteTextSuiteHistoryById(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getTextSuiteHistory"] });
    }
  });
};

// =========================================================================================

//Get all image suite history items endpoint.
const getImageSuiteHistory = async (data?: GetOptionsRequest) => {
  const params = new URLSearchParams();
  if (data?.StartDate) params.append("StartDate", data.StartDate);
  if (data?.EndDate) params.append("EndDate", data.EndDate);
  if (data?.AppIds) params.append("AppIds", data.AppIds.join(","));
  if (data?.Search) params.append("search", data.Search);

  const response = await Api.get(`/ImageSuiteHistory?${params.toString()}`);
  return response.data;
};

const useGetImageSuiteHistory = (filters?: GetOptionsRequest) => {
  return useQuery({
    queryKey: ["getImageSuiteHistory", filters],
    queryFn: () => getImageSuiteHistory(filters),
    refetchOnWindowFocus: false,
  });
};

//Get  image suite history filter apps endpoint.
const getImageSuiteHistoryApps = async () => {
  const response = await Api.get("/ImageSuite/Apps");
  return response.data;
};

const useGetImageSuiteHistoryApps = () => {
  return useQuery({
    queryKey: ["getImageSuiteHistoryApps"],
    queryFn: () => getImageSuiteHistoryApps(),
  });
};

//Delete all image suite history items endpoint.
const deleteImageSuiteHistory = async () => {
  const response = await Api.delete("/ImageSuiteHistory");
  return response.data;
};

const useDeleteImageSuiteHistory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteImageSuiteHistory"],
    mutationFn: () => deleteImageSuiteHistory(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getImageSuiteHistory"] });
    },
  });
};

//Get text image history item endpoint.
const getImageSuiteHistoryById = async (itemId: string) => {
  const response = await Api.get(`/ImageSuiteHistory/${itemId}`);
  return response.data;
};

const useGetImageSuiteHistoryById = (itemId: string) => {
  return useQuery({
    queryKey: ["getImageSuiteHistoryById",itemId],
    queryFn: () => getImageSuiteHistoryById(itemId),
    enabled:!!itemId.length
  });
};

//Delete image suite history item endpoint.
const deleteImageSuiteHistoryById = async (itemId: string) => {
  const response = await Api.delete(`/ImageSuiteHistory/${itemId}`);
  return response.data;
};

const useDeleteImageSuiteHistoryById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["deleteImageSuiteHistoryById"],
    mutationFn: (itemId: string) => deleteImageSuiteHistoryById(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getImageSuiteHistory"] });
    }
  });
};

//Regenerate image item endpoint.
const regenerateImageItem = async (data: RegenerateData) => {
  const response = await Api.post("ImageSuite/Regenerate", data);
  return response.data;
};

const useRegenerateImageItem = () => {
  return useMutation({
    mutationKey: ["regenerateImageItem"],
    mutationFn: (data: RegenerateData) => regenerateImageItem(data),
  });
};

//Get  suite history suggestion apps endpoint.
const suggestionSuiteApps = async () => {
  const response = await Api.get("/ImageSuiteHistory/Suggestions");
  return response.data;
};

const useSuggestionSuiteApps = () => {
  return useQuery({
    queryKey: ["suggestionSuiteApps"],
    queryFn: () => suggestionSuiteApps(),
  });
};

export {
  useGetTextSuiteHistory,
  useGetImageSuiteHistory,
  useGetImageSuiteHistoryById,
  useGetTextSuiteHistoryById,
  useDeleteImageSuiteHistoryById,
  useDeleteImageSuiteHistory,
  useDeleteTextSuiteHistory,
  useDeleteTextSuiteHistoryById,
  useGetTextSuiteHistoryApps,
  getImageSuiteHistoryApps,
  useGetImageSuiteHistoryApps,
  useRegenerateImageItem,
  useSuggestionSuiteApps,
};
